import { ApolloError } from "@apollo/client";
import { createContext, PropsWithChildren, useContext, useMemo } from "react";

import { Roles } from "src/core/constants/roles";
import { useCurrentUserWithRolesQuery, User } from "src/core/gql/graphql";

type UserContextValue = {
  user: User | null;
  loading: boolean;
  error: ApolloError | null;
  userGroups: Roles[];
  userName: string;
  userRole: string;
};

/**
 * @param user - L'utilisateur connecté.
 * @param loading - Indique si l'utilisateur est en cours de chargement.
 * @param error - Erreur de chargement de l'utilisateur.
 * @param userGroups - Les groupes de l'utilisateur.
 * @param userName - Le nom de l'utilisateur.
 * @param userRole - Le rôle de l'utilisateur.
 */
const UserContext = createContext<UserContextValue>({
  user: null,
  loading: true,
  error: null,
  userGroups: [],
  userName: "",
  userRole: "",
});

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }: PropsWithChildren) => {
  const { data, loading, error } = useCurrentUserWithRolesQuery({
    fetchPolicy: "network-only",
  });

  const user = data?.currentUser || null;
  const userGroups = useMemo<Roles[]>(
    () =>
      user?.groups?.map(
        (group: { definition: { code: string } }) => group.definition.code as Roles
      ) ?? [],
    [user]
  );
  const userName = user ? `${user.firstName} ${user.lastName}` : "";
  const userRole =
    user?.groups
      ?.map((group: { definition: { name: string } }) => group.definition.name)
      .join(", ") || "";

  const value: UserContextValue = useMemo(
    () => ({
      user: user as User,
      loading,
      error: error || null,
      userGroups,
      userName,
      userRole,
    }),
    [user, loading, error, userGroups, userName, userRole]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
